import type { SettingsField } from './middleware-settings';
import type { OdooAbsenceCategory, OdooModuleId } from './odoo-timeoff';

/**
 * Odoo Time Off settings (plan §4). Connection settings on the middleware
 * (Admin › Integrations); ASB's custom fields and the absence keywords as module settings (Admin › Modules › ⚙), so
 * any Odoo 18 works: a custom field Odoo lacks switches only that part off (plan §5.1).
 */

/** An Odoo field or selection value: checked before it reaches a domain or field list. */
export const ODOO_FIELD_NAME_PATTERN = /^[a-z_][a-z0-9_]*$/;

const FIELD_NAME_RULE = {
  regex: ODOO_FIELD_NAME_PATTERN.source,
  message: 'Use lowercase letters, digits and _ only, like director_id',
};

/** Odoo connector settings (shared by the three modules). */
export const ODOO_SETTINGS: readonly SettingsField[] = [
  {
    key: 'baseUrl',
    type: 'url',
    label: 'Odoo server',
    help: 'For example https://school.odoo.com (also the only address TeachPartner calls)',
    required: true,
  },
  {
    key: 'database',
    type: 'text',
    label: 'Database',
    help: "The Odoo database name (Odoo's login needs it)",
    required: true,
  },
  {
    key: 'login',
    type: 'text',
    label: 'Integration user',
    help: 'Login of the dedicated Odoo user (Time Off Officer, Employees Officer), not a person',
    required: true,
  },
  {
    key: 'apiKey',
    type: 'secret',
    label: 'API key',
    help: "The integration user's API key (Odoo › My Profile › Account Security)",
    required: true,
    // Like PowerSchool's client secret: the Tenant Admin may copy it (audited, never displayed).
    copyable: true,
  },
  {
    key: 'schoolTimezone',
    type: 'timezone',
    label: 'School time zone',
    help: 'Decides which day is "today" and the day used by Who is absent',
    required: true,
    default: 'Asia/Bahrain',
  },
];

const ODOO_LEAVE_MODULE_SETTINGS: readonly SettingsField[] = [
  {
    key: 'directorField',
    type: 'text',
    label: 'Director field',
    help: 'Employee field naming the Director (ASB: director_id). Empty or not in Odoo: the row is hidden',
    required: false,
    default: 'director_id',
    pattern: FIELD_NAME_RULE,
    emptyMeansOff: true,
  },
  {
    key: 'directorState',
    type: 'text',
    label: 'Director approval state',
    help: 'Leave state shown as "Director Approval" (ASB: dir_approve). Empty: standard states only',
    required: false,
    default: 'dir_approve',
    pattern: FIELD_NAME_RULE,
    emptyMeansOff: true,
  },
];

const ODOO_REQUEST_MODULE_SETTINGS: readonly SettingsField[] = [
  {
    key: 'portalFlagField',
    type: 'text',
    label: 'Portal flag field',
    help: 'Leave type field that offers a type in the portal (ASB: use_in_portal). Empty or not in Odoo: every allocated type is offered',
    required: false,
    default: 'use_in_portal',
    pattern: FIELD_NAME_RULE,
    emptyMeansOff: true,
  },
];

/** The keyword setting of each absence category (comma-separated, matched in type names). */
export const ODOO_CATEGORY_KEYWORD_SETTINGS: Readonly<Record<OdooAbsenceCategory, string>> = {
  sick: 'sickKeywords',
  vacation: 'vacationKeywords',
  other: 'otherKeywords',
  working_elsewhere: 'workingElsewhereKeywords',
  school_closure: 'schoolClosureKeywords',
};

const keywords = (key: string, label: string, help: string, fallback?: string): SettingsField => ({
  key,
  type: 'text',
  label,
  help,
  required: false,
  ...(fallback === undefined ? {} : { default: fallback }),
  // Blank keeps the list empty: nothing falls in this category (plan §4).
  emptyMeansOff: true,
});

const ODOO_ABSENCES_MODULE_SETTINGS: readonly SettingsField[] = [
  keywords(
    'sickKeywords',
    'Sick: keywords',
    'Comma-separated words in leave type names',
    'sick, disability',
  ),
  keywords(
    'vacationKeywords',
    'Vacation: keywords',
    'Comma-separated words in leave type names',
    'annual, personal day',
  ),
  keywords(
    'otherKeywords',
    'Other: keywords',
    'Optional. Types matching these are Other even if another list matches; anything unmatched is Other too',
  ),
  keywords(
    'workingElsewhereKeywords',
    'Working elsewhere: keywords',
    'Comma-separated words in leave type names',
    'work from home, out of office',
  ),
  keywords(
    'schoolClosureKeywords',
    'School closure: keywords',
    'Comma-separated words in leave type names',
    'break, festival',
  ),
];

/** Module settings of the three Odoo modules (merged into MODULE_SETTINGS). */
export const ODOO_MODULE_SETTINGS: Readonly<Record<OdooModuleId, readonly SettingsField[]>> = {
  'odoo.leave': ODOO_LEAVE_MODULE_SETTINGS,
  'odoo.request': ODOO_REQUEST_MODULE_SETTINGS,
  'odoo.absences': ODOO_ABSENCES_MODULE_SETTINGS,
};
